import type { PostHog } from "posthog-js";

// Accept either env name: VITE_POSTHOG_TOKEN (canonical, used in prod) or
// VITE_POSTHOG_PROJECT_TOKEN (matches the "Project API Key" label PostHog shows
// for copy-paste). Canonical takes precedence when both are set.
const POSTHOG_TOKEN =
	import.meta.env.VITE_POSTHOG_TOKEN ||
	import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const POSTHOG_HOST =
	import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

// posthog-js is loaded lazily via dynamic import() inside initPostHog(). It is
// still code-split into its own chunk at build time, but that chunk is only
// downloaded and executed once init runs — never when PostHog is disabled
// (no token). The `import type` above is erased at build time.
let client: PostHog | null = null;
let initPromise: Promise<void> | null = null;
// Bumped on every reset() so an in-flight init can detect it was torn down
// (logout / provider unmount) before its dynamic import resolved.
let initToken = 0;
// Events captured after init was triggered (post-auth) but before the SDK
// finished loading — flushed once ready so nothing is silently dropped.
const pendingEvents: Array<{
	name: string;
	properties?: Record<string, unknown>;
}> = [];

// PostHog is enabled whenever a token is configured. The token is the single
// switch: which environment reports to which PostHog project is decided by the
// token that environment carries (prod token → prod project, dev token → dev
// project). Without a token, all analytics calls safely no-op — the SDK is
// never downloaded, initialized, or sent any data.
export function isPostHogEnabled(): boolean {
	return Boolean(POSTHOG_TOKEN);
}

// True only once init has successfully completed and the SDK is live.
export function isPostHogReady(): boolean {
	return client !== null;
}

export function initPostHog(): Promise<void> {
	if (!isPostHogEnabled()) {
		return Promise.resolve();
	}
	if (initPromise) {
		return initPromise;
	}

	const token = ++initToken;
	initPromise = (async () => {
		try {
			const { default: posthog } = await import("posthog-js");

			// Torn down (reset/logout) while the chunk was loading — abort without
			// touching shared state, which reset() (and any newer init) now owns.
			if (token !== initToken) {
				return;
			}

			// `disable_compression` is a valid runtime init option missing from
			// posthog-js's published types. Intersect it into the real options type
			// (instead of a blanket cast) so the rest of the config stays fully
			// type-checked. It's the Android fix: CapacitorHttp (required on iOS)
			// corrupts gzip-compressed request bodies there, so PostHog rejects
			// ingest/replay with HTTP 400 "invalid GZIP data" and drops every event.
			// Uncompressed bodies pass through intact on both platforms, CapacitorHttp
			// still enabled.
			type PostHogInitOptions = NonNullable<
				Parameters<typeof posthog.init>[1]
			> & {
				disable_compression?: boolean;
			};

			const initOptions = {
				api_host: POSTHOG_HOST,
				autocapture: true,
				// $pageview on initial load + every React Router navigation (SPA
				// history API), in addition to the descriptive `*_page_viewed`
				// events each section captures.
				capture_pageview: "history_change",
				// Privacy posture (BAA-covered): person profiles only created for
				// identified (authenticated) users; never auto-created for anon.
				person_profiles: "identified_only",
				// Autocapture: strip element text + attributes from captured events
				// so analytics payloads stay free of any user-rendered content.
				mask_all_text: true,
				mask_all_element_attributes: true,
				// Android fix: send request bodies uncompressed (see note above).
				disable_compression: true,
				// Session replay: opt-in masking via the `.ph-mask-content` class.
				// Text inside any element with that class is masked, and inputs
				// nested within are masked too. Passwords are masked everywhere.
				session_recording: {
					maskAllInputs: false,
					maskInputOptions: { password: true },
					maskTextSelector: ".ph-mask-content",
					maskInputFn: (text, element) => {
						if (element?.closest?.(".ph-mask-content")) {
							return "*".repeat(text.length);
						}
						return text;
					},
				},
			} satisfies PostHogInitOptions;

			posthog.init(POSTHOG_TOKEN as string, initOptions);

			client = posthog;

			// Flush anything captured while the SDK was loading
			for (const event of pendingEvents) {
				posthog.capture(event.name, event.properties);
			}
			pendingEvents.length = 0;
		} catch (error) {
			// Leave client null + allow a future retry so callers keep no-op'ing
			// instead of hitting an uninitialized SDK (bad host/token, blocked storage).
			if (token === initToken) {
				client = null;
				initPromise = null;
				pendingEvents.length = 0;
			}
			console.error("[PostHog] Failed to initialize:", error);
		}
	})();

	return initPromise;
}

// Thin wrappers so consumers never touch the raw SDK singleton directly.
export function capturePostHog(
	name: string,
	properties?: Record<string, unknown>,
): void {
	if (client) {
		client.capture(name, properties);
		return;
	}
	// Buffer only while an init is in flight (provider mounted post-auth).
	// Before init is triggered, or after reset(), analytics stays a no-op so it
	// never runs for unauthenticated sessions.
	if (initPromise) {
		pendingEvents.push({ name, properties });
	}
}

export function identifyPostHog(
	userId: string,
	properties?: Record<string, unknown>,
): void {
	client?.identify(userId, properties);
}

// Super properties: merged into EVERY captured event (autocapture included),
// so app traffic (isNative/platform) is distinguishable from web in queries.
export function registerPostHog(properties: Record<string, unknown>): void {
	client?.register(properties);
}

export function resetPostHog(): void {
	// Invalidate any in-flight init so it can't activate the SDK after teardown,
	// then fully stand down so analytics no-ops until the next authenticated init.
	initToken++;
	initPromise = null;
	pendingEvents.length = 0;
	if (client) {
		client.reset();
		client = null;
	}
}
